import { getBaseThemeName } from "@/features/branding";
import { useGetCurrentUserQuery, useGetUsersQuery } from "@/store/services/supabase";
import type { OrgKeyReceivedState } from "@/tools/aggregate/portfolio-export/hooks/useAnalysisListFilters";
import { useCanCreatePrivateSeverities } from "@/utils/hooks/useCanCreatePrivate";
import { useKeys } from "@/utils/hooks/useKeys";
import Loading3QuartersOutlined from "@ant-design/icons/es/icons/Loading3QuartersOutlined";
import { useLocalStorage } from "@mantine/hooks";
import * as Sentry from "@sentry/nextjs";
import { AlertProps, Button, Space, Spin } from "antd";
import Link from "next/link";
import { useEffect, useState } from "react";
const ORG_KEY_POLL_MS = 10_000;
interface OrgKeyAlertProps extends AlertProps {
  key: string;
}

/**
 * Portfolio's org key exchange: polls the current user until the org keys arrive, and gives the alerts and password
 * prompt to show meanwhile.
 */
export const useOrgKeyExchange = () => {
  const [keyPair] = useKeys();
  const canCreatePrivate = useCanCreatePrivateSeverities();
  const [orgKeyPollInterval, setOrgKeyPollInterval] = useState(ORG_KEY_POLL_MS);
  const {
    data: currentUser
  } = useGetCurrentUserQuery(undefined, {
    pollingInterval: orgKeyPollInterval
  });
  const {
    data: users
  } = useGetUsersQuery();

  // Portfolio's key, so keys that arrive are announced once, on whichever page is opened first.
  const [keysReceivedState, setKeysReceivedState] = useLocalStorage<OrgKeyReceivedState>({
    key: "orgKeyReceivedState",
    defaultValue: "unset"
  });
  // useLocalStorage reads storage in its own mount effect; declared after it, so
  // this runs once that read has landed in the same commit.
  const [hasReadKeysState, setHasReadKeysState] = useState(false);
  useEffect(() => setHasReadKeysState(true), []);
  const [alerts, setAlerts] = useState<OrgKeyAlertProps[]>([]);
  const [hasJustReceivedKeys, setHasJustReceivedKeys] = useState(false);
  const [isPendingKeysOpen, setIsPendingKeysOpen] = useState(false);

  // Stops polling once the org keys arrive, and resumes if they ever go away.
  useEffect(() => {
    setOrgKeyPollInterval(currentUser?.has_org_keys ? 0 : ORG_KEY_POLL_MS);
  }, [currentUser?.has_org_keys]);
  useEffect(() => {
    const orgSecretExists = !!users?.find(u => u.has_org_keys);
    if (users && !orgSecretExists) {
      Sentry.captureMessage(`orgSecretExists: No users with org secret found`, {
        tags: {
          section: "Encryption keys"
        },
        extra: {
          orgId: currentUser?.org_id,
          userId: currentUser?.id
        }
      });
      setAlerts([{
        key: "locked",
        type: "error",
        showIcon: true,
        message: "Encryption Keys Locked",
        description: "It looks like no one in your organisation currently holds the encryption keys required to access your secure files. As a result, these keys cannot be restored or shared with your account. Please contact XDI Support to reset your organisation’s encryption key. Important: Any files previously encrypted with your organisation's old keys will be lost."
      }]);
      return;
    }
    const newAlerts: OrgKeyAlertProps[] = [];

    // When user previously accessed page without key, but now has them, show notice.
    if (keyPair.user && currentUser?.has_org_keys && (keysReceivedState === "awaiting" || hasJustReceivedKeys)) {
      setHasJustReceivedKeys(true);
      setKeysReceivedState("received");
      newAlerts.push({
        key: "received",
        message: "Keys Received",
        description: <>
            Your organisation&apos;s encryption keys have been successfully received. You can now securely create and
            access shared files with other members of your group.
          </>,
        type: "success",
        showIcon: true,
        closable: !!keyPair.org,
        onClose: () => {
          setHasJustReceivedKeys(false);
        }
      });
    }
    if (keyPair.user && !currentUser?.has_org_keys) {
      // When the user accesses the page without org key, show an awaiting message.
      setKeysReceivedState("awaiting");
      newAlerts.push({
        key: "awaiting",
        message: <Space>
            <span>Awaiting Encryption Keys</span>
            <Spin className="mb-1" indicator={<Loading3QuartersOutlined spin />} size="small" />
          </Space>,
        description: <Space orientation="vertical">
            <span>
              You require your organisation&apos;s encryption keys to enable access to secure files. Please try again
              later or ask an authorised user from your organisation to log in and share the encryption keys.
              {canCreatePrivate && " In the meantime you can only create & access private files."}
            </span>
            {getBaseThemeName() !== "hkma" && <span>
                For more information about the XDI Hub&apos;s Encryption keys,{" "}
                <Link target="_blank" href="/faqs/what-are-encryption-keys-on-the-xdi-climate-risk-hub">
                  read this article
                </Link>
                .
              </span>}
            <Button onClick={() => setIsPendingKeysOpen(true)}>View Authorised Users List</Button>
          </Space>,
        type: "info",
        showIcon: true
      });
    }
    setAlerts(newAlerts);
  }, [currentUser, keyPair, keysReceivedState, hasJustReceivedKeys, users, canCreatePrivate]);
  return {
    currentUser,
    alerts,
    hasJustReceivedKeys,
    /** Until the org keys arrive, the user can only open their own private runs. */
    isAwaitingOrgKeys: keysReceivedState === "awaiting",
    /** Whether `isAwaitingOrgKeys` has been read from storage yet. */
    isReady: hasReadKeysState,
    isPendingKeysOpen,
    closePendingKeys: () => setIsPendingKeysOpen(false)
  };
};